import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Plus, Pencil, Trash2, Bike as BikeIcon, Crosshair, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { nome: "", indirizzo: "", lat: "", lng: "", raggio_geofence: 150, note: "" };

export default function Sedi() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("locations.manage");
  const [sedi, setSedi] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  const load = async () => { try { setSedi((await api.get("/sedi")).data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ nome: s.nome, indirizzo: s.indirizzo || "", lat: s.lat ?? "", lng: s.lng ?? "", raggio_geofence: s.raggio_geofence || 150, note: s.note || "" });
    setOpen(true);
  };

  const useMyPosition = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })); setLocating(false); toast.success("Posizione acquisita"); },
      () => { setLocating(false); toast.error("Permesso posizione negato. Inserisci le coordinate manualmente."); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome sede obbligatorio"); return; }
    const payload = {
      nome: form.nome.trim(), indirizzo: form.indirizzo, note: form.note,
      raggio_geofence: parseInt(form.raggio_geofence) || 150,
      lat: form.lat === "" ? null : parseFloat(form.lat),
      lng: form.lng === "" ? null : parseFloat(form.lng),
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/sedi/${editing.id}`, payload);
      else await api.post("/sedi", payload);
      toast.success("Sede salvata"); setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    try { await api.delete(`/sedi/${s.id}`); toast.success("Sede eliminata"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="sedi-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Gestione multi-sede e geofencing</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-3"><MapPin className="h-9 w-9 text-[#f98125]" /> SEDI</h1>
        </div>
        {canManage && (
          <button onClick={openNew} data-testid="add-sede-btn" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuova sede
          </button>
        )}
      </div>

      {sedi.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna sede. {canManage && 'Aggiungine una con "Nuova sede".'}</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {sedi.map((s) => (
            <div key={s.id} data-testid={`sede-card-${s.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 bg-[#f98125]/15 rounded-sm flex items-center justify-center"><MapPin className="h-5 w-5 text-[#f98125]" /></div>
                  <div>
                    <div className="font-semibold">{s.nome}</div>
                    <div className="text-xs text-zinc-500">{s.indirizzo || "—"}</div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(s)} data-testid={`edit-sede-${s.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`delete-sede-${s.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#141414] border-zinc-800">
                        <AlertDialogHeader><AlertDialogTitle>Eliminare la sede "{s.nome}"?</AlertDialogTitle><AlertDialogDescription>Le bici assegnate resteranno senza sede. Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(s)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between text-xs text-zinc-500">
                <span>{s.lat != null && s.lng != null ? `${Number(s.lat).toFixed(4)}, ${Number(s.lng).toFixed(4)} · raggio ${s.raggio_geofence}m` : "Coordinate non impostate"}</span>
                <span className="text-[#f98125] flex items-center gap-1"><BikeIcon className="h-3 w-3" /> {s.bike_count} bici</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><MapPin className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica sede" : "Nuova sede"}</DialogTitle>
            <DialogDescription>Le coordinate e il raggio definiscono il geofence per il rilevamento automatico via QR.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label><Input data-testid="sede-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Sede Centrale" /></div>
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-zinc-500">Indirizzo</Label><Input data-testid="sede-indirizzo" value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Latitudine</Label><Input data-testid="sede-lat" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. 45.4642" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Longitudine</Label><Input data-testid="sede-lng" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. 9.1900" /></div>
            <div className="sm:col-span-2">
              <button onClick={useMyPosition} disabled={locating} data-testid="sede-use-position" className="flex items-center gap-2 text-sm rounded-sm border border-zinc-700 px-3 py-2 hover:bg-zinc-800 transition-colors disabled:opacity-60">
                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4 text-[#f98125]" />} Usa la mia posizione attuale
              </button>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Raggio geofence (m)</Label><Input data-testid="sede-raggio" type="number" value={form.raggio_geofence} onChange={(e) => setForm({ ...form, raggio_geofence: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-zinc-500">Note</Label><Textarea data-testid="sede-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="sede-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-50">{saving ? "Salvataggio…" : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
